import {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
	forwardRef,
	useImperativeHandle,
} from 'react';
import MultiRangeSlider from 'multi-range-slider-react';

import './inputRange.scss';
import useLadyService from '@/services/LadyService';
import GuideAgency from '@/components/pages/guide/agency/GuideAgency';

const InputRange = forwardRef((props, ref) => {
	const {
		min,
		max,
		step = 10,
		inputIdLeft,
		inputIdRight,
		title,
		subtitle,
		clazz,
		name,
		price,
		onChange,
	} = props;

	const { t } = useLadyService();

	const [local_value, setLocalValue] = useState({ min, max });
	const timeoutRef = useRef(null);

	const guardMinMax = useCallback(
		(vMin, vMax) => {
			// Разрешаем равные значения
			let newMin = Math.max(min, Math.min(vMin, vMax));
			let newMax = Math.min(max, Math.max(vMax, newMin));
			return { min: newMin, max: newMax };
		},
		[min, max],
	);
	const handleRangeInput = (e) => {
		const { minValue, maxValue } = e;
		if (minValue !== local_value.min || maxValue !== local_value.max) {
			const guarded = guardMinMax(minValue, maxValue);
			setLocalValue(guarded);

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			timeoutRef.current = setTimeout(() => {
				timeoutRef.current = null;
				onInputBlur(guarded);
			}, 500);
		}
	};

	const resetButton = useMemo(() => {
		return min !== local_value.min || local_value.max !== max;
	}, [min, max, local_value]);

	const checkValues = useCallback(
		(values) => {
			let vMin = Math.max(min, values.min);
			let vMax = Math.min(max, values.max);

			return { min: vMin, max: vMax };
		},
		[min, max],
	);

	const onInputBlur = (updated_values = undefined) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		const checked = checkValues(updated_values ?? local_value);
		setLocalValue(checked);
		onChange(checked);
	};

	const resetValues = () => {
		setLocalValue({ min, max });
		onInputBlur({ min, max });
	};

	useImperativeHandle(ref, () => ({
		reset: () => {
			resetValues();
		},
		setValues: (min, max) => {
			// console.trace('=> change from outside: ', min, max);

			if (local_value.min !== min || local_value.max !== max) {
				setLocalValue({ min, max });
			}
		},
	}));

	const handleKeyPress = (event) => {
		if (event.key === 'Enter') {
			onInputBlur();
		}
	};

	useEffect(() => {
		if (local_value.min !== min || local_value.max !== max) {
			setLocalValue({ min, max });
			// resetValues();
		}
	}, [min, max]);

	return (
		<div className={`range${clazz ? ` ${clazz}` : ''}`}>
			<div className={'range__body'}>
				<div className={'range__head'}>
					<div className={'range__title'}>
						{title}
						{subtitle ? <span>{subtitle}</span> : ''}
					</div>
					{resetButton ? (
						<button
							type="button"
							onClick={resetValues}
							className={'header__reset range__default'}
						>
							{t('reset')}
						</button>
					) : null}
				</div>
				<MultiRangeSlider
					min={min}
					max={max}
					step={step}
					stepOnly={!!step}
					className={'range__input'}
					disabled={min === max}
					ruler={false}
					label={false}
					minValue={local_value.min}
					maxValue={local_value.max}
					onChange={(e) => {
						handleRangeInput(e);
					}}
				/>
				<div
					className={`range__inputs ${min === max ? ' _disabled' : ''}`}
				>
					<label
						className={price ? `input-price ${price}` : ''}
						htmlFor={inputIdLeft}
					>
						<input
							id={inputIdLeft}
							type="tel"
							step={step}
							value={local_value.min}
							autoComplete="off"
							name={name}
							onChange={(e) =>
								setLocalValue({
									min: Number(e.target.value),
									max,
								})
							}
							onBlur={() => onInputBlur()}
							onKeyDown={(e) => handleKeyPress(e)}
						/>
						{local_value.min}
					</label>

					<label
						className={price ? `input-price ${price}` : ''}
						htmlFor={inputIdRight}
					>
						<input
							id={inputIdRight}
							type="tel"
							step={step}
							value={local_value.max}
							autoComplete="off"
							name={name}
							onChange={(e) =>
								setLocalValue({
									max: Number(e.target.value),
									min,
								})
							}
							onBlur={() => onInputBlur()}
							onKeyDown={(e) => handleKeyPress(e)}
						/>
						{local_value.max}
					</label>
				</div>
			</div>
		</div>
	);
});
InputRange.displayName = 'InputRange';
export default InputRange;
